import { createJourneyShell, type JourneyShell } from "../journey/shell.js";
import { THEME_DEFINITIONS, CHOREOGRAPHY_DEFINITIONS } from "./atlas.js";
import { renderAudioControls, type AudioControls } from "./audio-controls.js";
import {
  createProceduralAudioController,
  type ProceduralAudioController,
} from "./audio.js";
import {
  createNorthStarEditor,
  type NorthStarEditorController,
  type NorthStarEditorInitialState,
} from "./editor.js";
import { loadSummerRainPack, type EditorMode } from "./pack.js";
import type { SceneRendererKind } from "./renderer-contract.js";
import {
  mountRendererHost,
  type RendererHost,
  type RendererHostDiagnostics,
  type RendererHostFactories,
} from "./renderer-host.js";
import { resolveScenePlan, type ScenePlan } from "./scene-plan.js";
import { mountSensorControls, type SensorControls } from "./sensor-controls.js";
import {
  createNorthStarSession,
  SESSION_RECOVERY_PREFIX,
  SESSION_STORAGE_KEY,
  type NorthStarSession,
  type NorthStarSessionContents,
  type NorthStarSessionRecord,
  type PendingSessionImportResult,
  type SessionJsonObject,
  type SessionJsonValue,
  type SessionSaveResult,
} from "./session.js";
import {
  createSoloGarden,
  reduceSoloGarden,
  validateSoloGardenState,
  type SoloGardenState,
} from "./solo-garden.js";
import { createStageRendererFactories } from "./stage-renderer-factories.js";
import {
  createWordTendingGame,
  reduceWordTending,
  type WordTarget,
  type WordTendingState,
} from "./word-tending.js";

const PACK_REF = "summer-rain@1.0.0#rain-at-eaves";
const FALLBACK_LINES = Object.freeze([
  "summer rain gathers",
  "under the cedar eaves",
  "one bell answers",
] as const);
const NEUTRAL_AMBIENCE = Object.freeze({ breeze: 0, rainBias: 0 });

type AppMode = EditorMode | "repair";
type ActiveGame = "solo-garden" | "word-tending" | null;
type WorkerRegistrar = (
  scriptURL: string | URL,
  options?: RegistrationOptions,
) => Promise<unknown>;

interface PublicationModule {
  mountPublication(slot: HTMLElement, localSessionApi: LocalSessionApi): void | Promise<void>;
}

interface SensorFrameSubscriptionOptions {
  readonly reducedMotion: boolean;
  readonly requestFrame: (callback: FrameRequestCallback) => number;
  readonly cancelFrame: (frame: number) => void;
  readonly tick: () => void;
}

export function createSensorFrameSubscription(
  options: SensorFrameSubscriptionOptions,
): () => void {
  if (options.reducedMotion) return (): void => undefined;
  let stopped = false;
  let frame: number | null = null;
  const onFrame: FrameRequestCallback = (): void => {
    frame = null;
    if (stopped) return;
    options.tick();
    if (!stopped) frame = options.requestFrame(onFrame);
  };
  frame = options.requestFrame(onFrame);
  return (): void => {
    if (stopped) return;
    stopped = true;
    if (frame !== null) options.cancelFrame(frame);
    frame = null;
  };
}

const PUBLICATION_MODULE_PATH = "/app/assets/north-star/publication.js";
let loadedPublicationModule: PublicationModule | null = null;
let pendingPublicationModule: Promise<PublicationModule> | null = null;
let publicationModuleAttempts = 0;

function loadPublicationModule(): Promise<PublicationModule> {
  if (loadedPublicationModule !== null) return Promise.resolve(loadedPublicationModule);
  if (pendingPublicationModule !== null) return pendingPublicationModule;
  const attempt = publicationModuleAttempts++;
  const specifier = attempt === 0
    ? PUBLICATION_MODULE_PATH
    : `${PUBLICATION_MODULE_PATH}?publication-retry=${attempt}`;
  const pending = import(specifier) as Promise<PublicationModule>;
  pendingPublicationModule = pending;
  void pending.then(
    (publication) => {
      if (pendingPublicationModule !== pending) return;
      loadedPublicationModule = publication;
      pendingPublicationModule = null;
    },
    () => {
      if (pendingPublicationModule === pending) pendingPublicationModule = null;
    },
  );
  return pending;
}

interface LocalSessionApi {
  snapshot(): NorthStarSessionRecord;
  save(): SessionSaveResult;
  exportJSON(): string;
  importJSON(raw: string): boolean;
}

interface Composition {
  readonly generation: number;
  readonly app: HTMLElement;
  readonly localSessionApi: LocalSessionApi;
  readonly publicationSlot: HTMLElement;
  readonly publicationStatus: HTMLElement;
  readonly workerStatus: HTMLElement;
  destroyed: boolean;
  revoked: boolean;
  publicationGeneration: number;
  publicationQueue: Promise<void>;
  destroy(): Promise<void>;
}

interface RetirementOwner {
  readonly generation: number;
  state: "constructing" | "active" | "retiring" | "failed" | "retired";
  readonly resources: {
    readonly editor: NorthStarEditorController | null;
    readonly audio: AudioControls | null;
    readonly sensors: SensorControls | null;
    readonly journey: JourneyShell | null;
    readonly renderers: ReadonlySet<RendererHost>;
  };
  destroyPromise: Promise<void> | null;
  cleanup: () => Promise<void>;
  errors: readonly unknown[];
}

let activeComposition: Composition | null = null;
let globalRetirementOwner: RetirementOwner | null = null;
let nextCompositionGeneration = 0;
let teardownBarrier: Promise<void> = Promise.resolve();
let teardownPending = false;
let remountRequested = false;

const initialRecord: NorthStarSessionRecord = Object.freeze({
  schemaVersion: 1,
  packRef: PACK_REF,
  draft: FALLBACK_LINES.join("\n"),
  editorMode: "compose",
  themeId: "washi-dawn",
  choreographyId: "courtyard",
  annotations: Object.freeze([]),
  games: Object.freeze({}),
  updatedAt: 0,
});

function requireElement<E extends Element>(root: ParentNode, selector: string): E {
  const element = root.querySelector(selector);
  if (element === null) throw new Error(`North Star requires ${selector}`);
  return element as E;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const key of Reflect.ownKeys(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (descriptor !== undefined && "value" in descriptor) deepFreeze(descriptor.value);
    }
    Object.freeze(value);
  }
  return value;
}

function linesFromDraft(draft: string): [string, string, string] {
  const lines = draft.split("\n");
  if (lines.length !== 3) return [...FALLBACK_LINES];
  return [lines[0] ?? "", lines[1] ?? "", lines[2] ?? ""];
}

function ownsComposition(owner: Composition | null, generation: number | undefined): boolean {
  return owner !== null
    && !owner.destroyed
    && !owner.revoked
    && owner.generation === generation
    && activeComposition === owner;
}

export async function registerNorthStarWorker(
  register?: WorkerRegistrar,
): Promise<unknown> {
  const owner = activeComposition;
  const generation = owner?.generation;
  try {
    const serviceWorker = navigator.serviceWorker;
    const registrar = register ?? (serviceWorker === undefined
      ? null
      : (scriptURL: string | URL, options?: RegistrationOptions) => serviceWorker.register(scriptURL, options));
    if (registrar === null) throw new Error("Service workers are unavailable");
    const registration = await registrar("/app/north-star-sw.js", { scope: "/app/" });
    if (owner !== null && ownsComposition(owner, generation)) {
      owner.workerStatus.textContent = "Local app ready; offline cache registered.";
    }
    return registration;
  } catch {
    if (owner !== null && ownsComposition(owner, generation)) {
      owner.workerStatus.textContent = "Local app ready; offline cache unavailable.";
    }
    return null;
  }
}

export async function mountTerminalPublication(
  importer: () => Promise<PublicationModule> = loadPublicationModule,
): Promise<boolean> {
  const owner = activeComposition;
  if (owner === null || owner.destroyed) return false;
  const ownerGeneration = owner.generation;
  const publicationGeneration = owner.publicationGeneration;
  const slot = owner.publicationSlot;
  const expectedParent = slot.parentNode;
  const localSessionApi = owner.localSessionApi;
  const ownsPublicationSlot = (): boolean =>
    ownsComposition(owner, ownerGeneration)
    && publicationGeneration === owner.publicationGeneration
    && expectedParent !== null
    && slot.isConnected
    && owner.app.contains(slot)
    && owner.app.querySelector("[data-north-star-publish-slot]") === slot
    && slot.parentNode === expectedParent;
  const task = owner.publicationQueue.then(async (): Promise<boolean> => {
    const restoreFocus = slot.contains(document.activeElement);
    const restorePublicationFocus = (): void => {
      const active = document.activeElement;
      if (
        restoreFocus
        && ownsComposition(owner, ownerGeneration)
        && (active === null || active === document.body || !active.isConnected)
      ) {
        owner.app.querySelector<HTMLElement>(
          'button[data-north-star-action="publication"]',
        )?.focus();
      }
    };
    try {
      if (!ownsPublicationSlot()) return false;
      const publication = await importer();
      if (!ownsPublicationSlot()) return false;
      await publication.mountPublication(slot, localSessionApi);
      restorePublicationFocus();
      return ownsPublicationSlot();
    } catch {
      if (ownsPublicationSlot()) {
        slot.replaceChildren();
        owner.publicationStatus.textContent =
          "Publication is unavailable. Your local session is still ready.";
      }
      restorePublicationFocus();
      return false;
    }
  });
  owner.publicationQueue = task.then(() => undefined, () => undefined);
  return task;
}

function firstWordTarget(lines: readonly string[]): WordTarget | null {
  for (let line = 0; line < lines.length; line += 1) {
    const points = Array.from(lines[line] ?? "");
    const start = points.findIndex((point) => !/\s/u.test(point));
    if (start < 0) continue;
    let end = start + 1;
    while (end < points.length && !/\s/u.test(points[end] ?? "")) end += 1;
    return Object.freeze({ line, start, end });
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${stableJson(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "undefined";
}

function replayWordOperation(
  state: WordTendingState,
  operation: Record<string, unknown>,
): WordTendingState {
  if (operation.kind === "keep") {
    return reduceWordTending(state, { type: "keep", target: operation.target });
  }
  if (operation.kind === "move") {
    return reduceWordTending(state, {
      type: "move",
      target: operation.target,
      destination: operation.destination,
    });
  }
  if (operation.kind === "offer") {
    return reduceWordTending(state, {
      type: "offer",
      target: operation.target,
      toPlayer: operation.toPlayer,
    });
  }
  throw new TypeError("Unknown Word Tending operation");
}

function validateWordTendingState(value: SessionJsonValue | undefined): WordTendingState | null {
  if (!isRecord(value)
      || value.kind !== "word-tending"
      || value.schemaVersion !== 1
      || !Array.isArray(value.players)
      || !Array.isArray(value.originLines)
      || !Array.isArray(value.lines)
      || !Array.isArray(value.history)
      || typeof value.phase !== "string") return null;
  try {
    let replayed = createWordTendingGame({
      seed: value.seed,
      players: value.players,
      lines: value.originLines,
    });
    for (const entry of value.history) {
      if (!isRecord(entry) || !isRecord(entry.operation)) return null;
      replayed = reduceWordTending(replayed, { type: "reveal" });
      replayed = replayWordOperation(replayed, entry.operation);
      replayed = reduceWordTending(replayed, { type: "handoff" });
    }
    if (value.phase === "revealed") {
      replayed = reduceWordTending(replayed, { type: "reveal" });
    } else if (value.phase === "operated") {
      if (!isRecord(value.pendingOperation)
          || !isRecord(value.pendingOperation.operation)) return null;
      replayed = reduceWordTending(replayed, { type: "reveal" });
      replayed = replayWordOperation(replayed, value.pendingOperation.operation);
    } else if (value.phase !== "concealed" && value.phase !== "complete") {
      return null;
    }
    if (stableJson(replayed) !== stableJson(value)) return null;
    return replayed;
  } catch {
    return null;
  }
}

function hasSummerRainPack(raw: string): boolean | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || typeof parsed.packRef !== "string") return null;
    return parsed.packRef === PACK_REF;
  } catch {
    return null;
  }
}

function hasValidImportedGames(raw: string): boolean {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !isRecord(parsed.games)) return true;
    const solo = parsed.games.soloGarden;
    if (solo !== undefined) validateSoloGardenState(solo);
    const word = parsed.games.wordTending;
    if (word !== undefined && validateWordTendingState(word as SessionJsonValue) === null) {
      return false;
    }
    const active = parsed.games.activeGame;
    if (active !== undefined
        && active !== "solo-garden"
        && active !== "word-tending"
        && active !== null) return false;
    if (active === "solo-garden" && solo === undefined) return false;
    if (active === "word-tending" && word === undefined) return false;
    return true;
  } catch {
    return false;
  }
}

const MAX_PERSISTED_GAME_RECOVERY_PROBES = 1_000;

type PersistedGameRecoveryResult = Readonly<{
  status: "none" | "recovered" | "clock-error" | "storage-error" | "collision-exhausted";
  malformedRaw: string | null;
  persisted: boolean;
  key?: string;
  error?: string;
}>;

function hasMalformedPersistedGames(raw: string): boolean {
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed)
      && parsed.packRef === PACK_REF
      && isRecord(parsed.games)
      && !hasValidImportedGames(raw);
  } catch {
    return false;
  }
}

function recoverMalformedPersistedGames(
  storage: Storage,
  clock: () => number,
): PersistedGameRecoveryResult {
  let raw: string | null;
  try {
    raw = storage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return Object.freeze({status: "none", malformedRaw: null, persisted: false});
  }
  if (raw === null || !hasMalformedPersistedGames(raw)) {
    return Object.freeze({status: "none", malformedRaw: null, persisted: false});
  }

  let timestamp: number;
  try {
    timestamp = clock();
  } catch (error) {
    return Object.freeze({
      status: "clock-error",
      malformedRaw: raw,
      persisted: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
  if (!Number.isSafeInteger(timestamp) || timestamp < 0) {
    return Object.freeze({
      status: "clock-error",
      malformedRaw: raw,
      persisted: false,
      error: "The local clock did not provide a valid recovery timestamp.",
    });
  }
  const baseKey = `${SESSION_RECOVERY_PREFIX}${timestamp}`;
  for (let suffix = 0; suffix < MAX_PERSISTED_GAME_RECOVERY_PROBES; suffix += 1) {
    const recoveryKey = suffix === 0 ? baseKey : `${baseKey}:${suffix}`;
    try {
      if (storage.getItem(recoveryKey) !== null) continue;
    } catch (error) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw: raw,
        persisted: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    try {
      storage.setItem(recoveryKey, raw);
    } catch (error) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw: raw,
        persisted: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    try {
      if (storage.getItem(recoveryKey) !== raw) {
        return Object.freeze({
          status: "storage-error",
          malformedRaw: raw,
          persisted: false,
          error: "The malformed local game archive could not be verified.",
        });
      }
    } catch (error) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw: raw,
        persisted: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    try {
      storage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw: raw,
        persisted: true,
        key: recoveryKey,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    try {
      if (storage.getItem(SESSION_STORAGE_KEY) !== null) {
        return Object.freeze({
          status: "storage-error",
          malformedRaw: raw,
          persisted: true,
          key: recoveryKey,
          error: "The malformed primary local game record could not be removed.",
        });
      }
    } catch (error) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw: raw,
        persisted: true,
        key: recoveryKey,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    return Object.freeze({
      status: "recovered",
      malformedRaw: raw,
      persisted: true,
      key: recoveryKey,
    });
  }
  return Object.freeze({
    status: "collision-exhausted",
    malformedRaw: raw,
    persisted: false,
    error: "All 1,000 malformed local game recovery keys are occupied.",
  });
}

function retryRetainedMalformedRecovery(
  storage: Storage,
  retained: PersistedGameRecoveryResult,
): PersistedGameRecoveryResult | null {
  const malformedRaw = retained.malformedRaw;
  const recoveryKey = retained.key;
  if (malformedRaw === null || !retained.persisted || recoveryKey === undefined) return null;

  try {
    if (storage.getItem(recoveryKey) !== malformedRaw) return null;
  } catch {
    return null;
  }
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    return Object.freeze({
      status: "storage-error",
      malformedRaw,
      persisted: true,
      key: recoveryKey,
      error: error instanceof Error ? error.message : String(error),
    });
  }
  try {
    if (storage.getItem(SESSION_STORAGE_KEY) !== null) {
      return Object.freeze({
        status: "storage-error",
        malformedRaw,
        persisted: true,
        key: recoveryKey,
        error: "The malformed primary local game record could not be removed.",
      });
    }
  } catch (error) {
    return Object.freeze({
      status: "storage-error",
      malformedRaw,
      persisted: true,
      key: recoveryKey,
      error: error instanceof Error ? error.message : String(error),
    });
  }
  return Object.freeze({
    status: "recovered",
    malformedRaw,
    persisted: true,
    key: recoveryKey,
  });
}
export function mountNorthStar(): Composition | null {
  if (activeComposition !== null && !activeComposition.destroyed) return activeComposition;
  if (teardownPending
      || (globalRetirementOwner !== null
        && globalRetirementOwner.state !== "active"
        && globalRetirementOwner.state !== "retired")) {
    remountRequested = true;
    return null;
  }
  const app = document.querySelector<HTMLElement>("[data-north-star-app]");
  if (app === null) return null;

  const canonicalItems = Array.from(
    app.querySelectorAll<HTMLElement>("[data-north-star-draft-line]"),
  );
  if (canonicalItems.length !== 3) throw new Error("North Star requires exactly three poem lines");

  const editorRoot = requireElement<HTMLElement>(app, "[data-north-star-editor]");
  const sessionStatus = requireElement<HTMLElement>(app, "[data-north-star-session-status]");
  const modeStatus = requireElement<HTMLElement>(app, "[data-north-star-mode-status]");
  const gameHost = requireElement<HTMLElement>(app, "[data-north-star-game-host]");
  const gameStatus = requireElement<HTMLElement>(app, "[data-north-star-game-status]");
  const rendererSelect = requireElement<HTMLSelectElement>(app, "[data-north-star-renderer]");
  const importInput = requireElement<HTMLInputElement>(app, "[data-north-star-import]");
  const rendererLabels: Record<string, string> = {
    three: "Auto fallback",
    canvas: "Canvas fallback",
    dom: "Text fallback",
  };
  for (const option of rendererSelect.options) {
    option.textContent = rendererLabels[option.value] ?? option.textContent;
  }
  const importName = document.createElement("output");
  importName.dataset.northStarImportName = "";
  importName.setAttribute("aria-live", "polite");
  importName.textContent = "No file selected.";
  importInput.closest("label")?.insertAdjacentElement("afterend", importName);
  const importRepairHost = requireElement<HTMLElement>(app, "[data-north-star-import-repair]");
  const publicationSlot = requireElement<HTMLElement>(app, "[data-north-star-publish-slot]");
  const workerStatus = requireElement<HTMLElement>(app, "[data-north-star-worker-status]");

  const cleanups: Array<() => void> = [];
  cleanups.push(() => importName.remove());
  const ownership: { composition: Composition | null } = { composition: null };
  const hasAuthority = (): boolean =>
    ownership.composition !== null
    && activeComposition === ownership.composition
    && !ownership.composition.revoked
    && !ownership.composition.destroyed;
  const requireAuthority = (): void => {
    if (!hasAuthority()) throw new Error("North Star composition is retired");
  };
  const listen = <K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void,
  ): void => {
    const eventListener: EventListener = ((event: Event) => {
      if (hasAuthority()) (listener as EventListener)(event);
    });
    target.addEventListener(type, eventListener);
    cleanups.push(() => target.removeEventListener(type, eventListener));
  };

  let malformedPersistedGamesRecovery = recoverMalformedPersistedGames(
    window.localStorage,
    Date.now,
  );
  let foreignPersistedSession = false;
  const protectMalformedPrimaryWrite = (key: string): void => {
    if (key !== SESSION_STORAGE_KEY
        || malformedPersistedGamesRecovery.malformedRaw === null
        || malformedPersistedGamesRecovery.status === "recovered") return;
    const malformedRaw = malformedPersistedGamesRecovery.malformedRaw;
    const retainedRecovery = malformedPersistedGamesRecovery;
    const retried = retryRetainedMalformedRecovery(
      window.localStorage,
      retainedRecovery,
    ) ?? recoverMalformedPersistedGames(window.localStorage, Date.now);
    if (retried.status === "recovered") {
      malformedPersistedGamesRecovery = retried;
      return;
    }
    if (retried.status === "none"
        && retainedRecovery.persisted
        && retainedRecovery.key !== undefined) {
      try {
        if (window.localStorage.getItem(retainedRecovery.key) === malformedRaw) {
          malformedPersistedGamesRecovery = Object.freeze({
            status: "recovered",
            malformedRaw,
            persisted: true,
            key: retainedRecovery.key,
          });
          return;
        }
      } catch {
        // The primary remains protected unless the retained archive is exactly reverified.
      }
    }
    malformedPersistedGamesRecovery = retried.malformedRaw === null
      ? Object.freeze({
        status: "storage-error",
        malformedRaw,
        persisted: false,
        error: "The malformed primary record could not be read for recovery retry.",
      })
      : retried;
    throw new Error(
      "The exact malformed primary record is not archived; this save remains memory-only.",
    );
  };
  const guardedStorage = Object.freeze({
    getItem(key: string): string | null {
      if (key === SESSION_STORAGE_KEY
          && malformedPersistedGamesRecovery.malformedRaw !== null) return null;
      const raw = window.localStorage.getItem(key);
      if (raw !== null && hasSummerRainPack(raw) === false) {
        foreignPersistedSession = true;
        return null;
      }
      return raw;
    },
    setItem(key: string, value: string): void {
      protectMalformedPrimaryWrite(key);
      window.localStorage.setItem(key, value);
    },
    removeItem(key: string): void { window.localStorage.removeItem(key); },
  });
  const session: NorthStarSession = createNorthStarSession({
    initial: initialRecord,
    storage: guardedStorage,
    clock: Date.now,
  });
  let draftLines = linesFromDraft(session.state.draft);
  let draftDirty = false;
  let appMode: AppMode = session.state.editorMode;
  let editor: NorthStarEditorController | null = null;
  let activeRenderer: RendererHost | null = null;
  let activeScenePlan: ScenePlan | null = null;
  let sceneApplyCount = 0;
  let rendererPreference = rendererSelect.value as SceneRendererKind;
  const forcedRendererFailures = new Set<SceneRendererKind>();
  const baseRendererFactories = createStageRendererFactories();
  let soloGarden: SoloGardenState | null = null;
  let wordTending: WordTendingState | null = null;
  let activeGame: ActiveGame = null;
  let pendingImport: PendingSessionImportResult | null = null;
  let importGeneration = 0;
  let exportUrl: string | null = null;
  let packStatus: "loading" | "ready" | "fallback" = "loading";
  let journey: JourneyShell | null = null;
  let audioControls: AudioControls | null = null;
  let audioController: ProceduralAudioController | null = null;
  let sensorControls: SensorControls | null = null;
  let stopSensorFrames: (() => void) | null = null;
  let sensorMotionQuery: MediaQueryList | null = null;
  let sensorMotionListener: ((event: MediaQueryListEvent) => void) | null = null;
  let annotationCounter = 0;
  let destroyPromise: Promise<void> | null = null;
  let resolveRetirement: (() => void) | null = null;
  const ownedRenderers = new Set<RendererHost>();
  const terminalCleanupFailures = new Set<"editor" | "journey">();

  const generation = nextCompositionGeneration + 1;
  const composition: Composition = {
    generation,
    app,
    localSessionApi: Object.freeze({
      snapshot: (): NorthStarSessionRecord => { requireAuthority(); return session.state; },
      save: (): SessionSaveResult => { requireAuthority(); return saveSession(); },
      exportJSON: (): string => { requireAuthority(); return session.exportJSON(); },
      importJSON: (raw: string): boolean => { requireAuthority(); return importSession(raw); },
    }),
    publicationSlot,
    publicationStatus: sessionStatus,
    workerStatus,
    destroyed: false,
    revoked: false,
    publicationGeneration: 0,
    publicationQueue: Promise.resolve(),
    destroy,
  };
  ownership.composition = composition;
  const retirementOwner: RetirementOwner = {
    generation,
    state: "constructing",
    resources: {
      get editor(): NorthStarEditorController | null { return editor; },
      get audio(): AudioControls | null { return audioControls; },
      get sensors(): SensorControls | null { return sensorControls; },
      get journey(): JourneyShell | null { return journey; },
      renderers: ownedRenderers,
    },
    destroyPromise: null,
    cleanup: () => composition.destroy(),
    errors: Object.freeze([]),
  };

  const syncCanonicalDom = (): void => {
    canonicalItems.forEach((item, index) => {
      const line = draftLines[index] ?? "";
      if (item.textContent !== line) item.textContent = line;
      item.contentEditable = appMode === "compose" ? "true" : "false";
      item.setAttribute("aria-label", `Poem line ${index + 1}`);
    });
  };

  const currentGames = (): SessionJsonObject => {
    const games: Record<string, SessionJsonValue> = {};
    if (soloGarden !== null) games.soloGarden = soloGarden as unknown as SessionJsonValue;
    if (wordTending !== null) games.wordTending = wordTending as unknown as SessionJsonValue;
    if (activeGame !== null) games.activeGame = activeGame;
    return Object.freeze(games);
  };

  const editorInitial = (mode: EditorMode): NorthStarEditorInitialState => {
    const lines: [string, string, string] = [draftLines[0], draftLines[1], draftLines[2]];
    return Object.freeze({
      packRef: session.state.packRef,
      lines: Object.freeze(lines),
      mode,
      themeId: session.state.themeId as NorthStarEditorInitialState["themeId"],
      choreographyId:
        session.state.choreographyId as NorthStarEditorInitialState["choreographyId"],
      annotations: session.state.annotations,
      games: currentGames(),
    });
  };

  const rendererFactories = (): RendererHostFactories => Object.freeze({
    three: forcedRendererFailures.has("three")
      ? () => { throw new Error("NorthStarDebug forced Three fallback"); }
      : baseRendererFactories.three,
    canvas: forcedRendererFailures.has("canvas")
      ? () => { throw new Error("NorthStarDebug forced Canvas fallback"); }
      : baseRendererFactories.canvas,
    dom: forcedRendererFailures.has("dom")
      ? () => { throw new Error("NorthStarDebug forced DOM terminal fallback"); }
      : baseRendererFactories.dom,
  });

  const mountEditor = (mode: EditorMode): void => {
    const restoreFocus = editor?.element.contains(document.activeElement) === true;
    editor?.destroy();
    activeRenderer = null;
    activeScenePlan = null;
    editor = createNorthStarEditor({
      root: editorRoot,
      initial: editorInitial(mode),
      availability: {
        themeIds: THEME_DEFINITIONS.map((theme) => theme.id),
        choreographyIds: CHOREOGRAPHY_DEFINITIONS.map((choreography) => choreography.id),
      },
      session: Object.freeze({
        save(contents: NorthStarSessionContents): SessionSaveResult {
          requireAuthority();
          return session.save(Object.freeze({ ...contents, games: currentGames() }));
        },
      }),
      getSelection: () => window.getSelection(),
      renderer: {
        mount(root, scenePlan): Pick<RendererHost, "destroy"> {
          for (const retiredHost of ownedRenderers) {
            if (!retiredHost.diagnostics().destroyed) {
              throw new Error("A previous Stage renderer is still retiring");
            }
            ownedRenderers.delete(retiredHost);
          }
          const host = mountRendererHost({
            root,
            scenePlan,
            preferred: rendererPreference,
            factories: rendererFactories(),
          });
          if (host.diagnostics().kind === null) {
            host.destroy();
            throw new Error("No Stage renderer is available");
          }
          activeRenderer = host;
          ownedRenderers.add(host);
          activeScenePlan = scenePlan;
          let retired = false;
          return Object.freeze({
            destroy(): void {
              if (retired) return;
              retired = true;
              host.destroy();
              if (!host.diagnostics().destroyed) {
                throw new Error("Renderer host cleanup is incomplete");
              }
              ownedRenderers.delete(host);
              if (activeRenderer === host) {
                activeRenderer = null;
                activeScenePlan = null;
              }
            },
          });
        },
      },
    });
    draftDirty = false;
    if (restoreFocus && document.activeElement === document.body) {
      editor.element.querySelector<HTMLElement>(
        '[tabindex="0"], input:not(:disabled), button:not(:disabled), select:not(:disabled)',
      )?.focus();
    }
  };

  const refreshStage = (): boolean => {
    if (appMode !== "stage") return true;
    try {
      mountEditor("stage");
      modeStatus.textContent = `Stage renderer: ${activeRenderer?.diagnostics().kind ?? "unavailable"}`;
      return true;
    } catch (error) {
      modeStatus.textContent = `Stage refresh failed: ${errorMessage(error)}`;
      return false;
    }
  };

  const modeLabel = (mode: AppMode): string =>
    `${mode[0]?.toUpperCase() ?? ""}${mode.slice(1)}`;

  const modeButtons = Array.from(
    app.querySelectorAll<HTMLButtonElement>("button[data-north-star-mode]"),
  );
  const renderMode = (announcement?: string): void => {
    app.dataset.mode = appMode;
    syncCanonicalDom();
    for (const button of modeButtons) {
      const selected = button.dataset.northStarMode === appMode;
      button.setAttribute("aria-pressed", String(selected));
      button.tabIndex = selected ? 0 : -1;
    }
    modeStatus.textContent = announcement ?? (appMode === "stage"
      ? `Stage renderer: ${activeRenderer?.diagnostics().kind ?? "unavailable"}`
      : `${modeLabel(appMode)} mode`);
  };

  const setMode = (requested: AppMode): boolean => {
    if (!hasAuthority()) return false;
    if (requested === appMode) return true;
    const previous = appMode;
    const editorMode: EditorMode = requested === "repair" ? "annotate" : requested;
    let transitioned = false;
    try {
      if (editor?.mode === editorMode) transitioned = true;
      else if (editor === null || draftDirty) {
        mountEditor(editorMode);
        transitioned = true;
      } else transitioned = editor.setMode(editorMode);
    } catch {
      transitioned = false;
    }
    if (!transitioned) {
      modeStatus.textContent = `${modeLabel(requested)} unavailable; stayed in ${modeLabel(previous)}.`;
      return false;
    }
    appMode = requested;
    if (requested === "repair") {
      renderMode(editor?.state.repairPending === true
        ? "Repair tray ready for annotation decisions."
        : "Annotations checked; no repairs are needed.");
    } else if (requested === "stage") {
      renderMode();
    } else renderMode();
    return true;
  };

  function sessionContents(): NorthStarSessionContents {
    const state = editor?.state;
    return Object.freeze({
      packRef: state?.packRef ?? session.state.packRef,
      draft: draftLines.join("\n"),
      editorMode: state?.mode ?? session.state.editorMode,
      themeId: state?.themeId ?? session.state.themeId,
      choreographyId: state?.choreographyId ?? session.state.choreographyId,
      annotations: state?.annotations ?? session.state.annotations,
      games: currentGames(),
    });
  }

  function saveSession(): SessionSaveResult {
    const result = session.save(sessionContents());
    sessionStatus.textContent = result.status === "committed"
      ? "Saved locally."
      : result.status === "memory-only"
        ? "Saved in memory; local storage is unavailable."
        : "Save paused because the local clock is unavailable.";
    return result;
  }

  const renderSoloGarden = (): void => {
    if (soloGarden === null) return;
    gameHost.replaceChildren();
    const heading = document.createElement("h3");
    heading.textContent = "Solo Garden";
    gameHost.append(heading);
    if (soloGarden.turn < 3) {
      for (const operation of ["water", "shelter", "open"] as const) {
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.soloOperation = operation;
        button.dataset.northStarAction = "solo-tend";
        button.textContent = `${operation[0]?.toUpperCase() ?? ""}${operation.slice(1)}`;
        button.addEventListener("click", () => {
          if (!hasAuthority()) return;
          if (soloGarden === null) return;
          soloGarden = reduceSoloGarden(soloGarden, {
            type: "tend",
            turn: soloGarden.turn + 1,
            operation,
          });
          renderSoloGarden();
        }, { once: true });
        gameHost.append(button);
      }
    }
    gameStatus.textContent = soloGarden.completion?.summary
      ?? `Turn ${soloGarden.turn} of 3. Choose how to tend the poem.`;
  };

  const renderWordTending = (): void => {
    if (wordTending === null) return;
    gameHost.replaceChildren();
    const heading = document.createElement("h3");
    heading.textContent = "Word Tending";
    gameHost.append(heading);
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.northStarAction = "word-turn";
    if (wordTending.phase === "concealed") {
      button.dataset.wordAction = "reveal";
      button.textContent = "Reveal next keeper";
      button.addEventListener("click", () => {
        if (!hasAuthority()) return;
        if (wordTending === null) return;
        wordTending = reduceWordTending(wordTending, { type: "reveal" });
        renderWordTending();
      }, { once: true });
      gameHost.append(button);
    } else if (wordTending.phase === "revealed") {
      const target = firstWordTarget(wordTending.lines);
      if (target !== null) {
        button.dataset.wordAction = "keep";
        button.textContent = "Keep next word";
        button.addEventListener("click", () => {
          if (!hasAuthority()) return;
          if (wordTending === null) return;
          wordTending = reduceWordTending(wordTending, { type: "keep", target });
          renderWordTending();
        }, { once: true });
        gameHost.append(button);
      }
    } else if (wordTending.phase === "operated") {
      button.dataset.wordAction = "handoff";
      button.textContent = "Confirm and hand off";
      button.addEventListener("click", () => {
        if (!hasAuthority()) return;
        if (wordTending === null) return;
        wordTending = reduceWordTending(wordTending, { type: "handoff" });
        renderWordTending();
      }, { once: true });
      gameHost.append(button);
    }
    gameStatus.textContent = wordTending.summary
      ?? `Turn ${wordTending.turn} of 6. ${wordTending.phase}.`;
  };

  const renderActiveGame = (): void => {
    if (activeGame === "solo-garden" && soloGarden !== null) renderSoloGarden();
    else if (activeGame === "word-tending" && wordTending !== null) renderWordTending();
    else {
      activeGame = null;
      gameHost.replaceChildren();
      gameStatus.textContent = "Choose a local game.";
    }
  };

  const hydrateGames = (games: SessionJsonObject): void => {
    try {
      soloGarden = games.soloGarden === undefined
        ? null
        : validateSoloGardenState(games.soloGarden);
    } catch {
      soloGarden = null;
    }
    wordTending = validateWordTendingState(games.wordTending);
    const storedActive = games.activeGame;
    if (storedActive === "solo-garden" && soloGarden !== null) activeGame = storedActive;
    else if (storedActive === "word-tending" && wordTending !== null) activeGame = storedActive;
    else if (wordTending !== null) activeGame = "word-tending";
    else if (soloGarden !== null) activeGame = "solo-garden";
    else activeGame = null;
    renderActiveGame();
  };

  function resumeState(message: string): void {
    draftLines = linesFromDraft(session.state.draft);
    draftDirty = false;
    appMode = session.state.editorMode;
    rendererPreference = rendererSelect.value as SceneRendererKind;
    hydrateGames(session.state.games);
    mountEditor(session.state.editorMode);
    renderMode();
    sessionStatus.textContent = message;
  }

  const clearPendingImport = (): void => {
    pendingImport = null;
    importRepairHost.hidden = true;
    importRepairHost.replaceChildren();
  };

  const decidePendingImport = (choice: "use" | "discard"): void => {
    if (!hasAuthority()) return;
    const pending = pendingImport;
    if (pending === null) return;
    const restoreFocus = importRepairHost.contains(document.activeElement);
    const decision = choice === "use" ? pending.commitRepaired() : pending.discardAffected();
    if (decision.status === "stale" || decision.status === "discarded") {
      sessionStatus.textContent = "Import was superseded by a newer local session.";
      clearPendingImport();
      if (restoreFocus) importInput.focus();
      return;
    }
    clearPendingImport();
    resumeState(choice === "use"
      ? "Imported with repaired annotations."
      : "Imported after discarding affected annotations.");
    if (restoreFocus) importInput.focus();
  };

  const showPendingImport = (pending: PendingSessionImportResult): void => {
    pendingImport?.discard();
    pendingImport = pending;
    const heading = document.createElement("h3");
    heading.textContent = "Imported annotations need repair";
    const detail = document.createElement("p");
    detail.textContent = `${pending.repair.repairs.length} annotation repair decision(s).`;
    const use = document.createElement("button");
    use.type = "button";
    use.dataset.importRepairChoice = "use";
    use.textContent = "Use repaired annotations";
    use.addEventListener("click", () => decidePendingImport("use"), { once: true });
    const discard = document.createElement("button");
    discard.type = "button";
    discard.dataset.importRepairChoice = "discard";
    discard.textContent = "Discard affected annotations";
    discard.addEventListener("click", () => decidePendingImport("discard"), { once: true });
    importRepairHost.replaceChildren(heading, detail, use, discard);
    importRepairHost.hidden = false;
    sessionStatus.textContent = "Import paused for an explicit annotation repair decision.";
  };

  function importSession(raw: string): boolean {
    if (!hasAuthority()) return false;
    if (hasSummerRainPack(raw) === false) {
      sessionStatus.textContent = "Import rejected: this session is not the Summer Rain pack.";
      return false;
    }
    if (!hasValidImportedGames(raw)) return false;
    const result = session.importJSON(raw);
    if (result.status === "invalid") {
      sessionStatus.textContent = `Import rejected: ${result.error}`;
      return false;
    }
    if (result.status === "repair-pending") {
      showPendingImport(result);
      return false;
    }
    clearPendingImport();
    resumeState("Imported and resumed locally.");
    return true;
  }

  const applySensorAmbience = (): void => {
    if (activeRenderer === null || activeScenePlan === null || sensorControls === null) return;
    const snapshot = sensorControls.controller.status() === "enabled"
      ? sensorControls.controller.snapshot()
      : NEUTRAL_AMBIENCE;
    if (snapshot.breeze === activeScenePlan.ambience.breeze
        && snapshot.rainBias === activeScenePlan.ambience.rainBias) return;
    try {
      const theme = THEME_DEFINITIONS.find(
        ({ id }) => id === (editor?.state.themeId ?? session.state.themeId),
      );
      const choreography = CHOREOGRAPHY_DEFINITIONS.find(
        ({ id }) => id === (editor?.state.choreographyId ?? session.state.choreographyId),
      );
      if (theme === undefined || choreography === undefined) {
        throw new Error("The active scene selection is unavailable");
      }
      const nextPlan = resolveScenePlan({
        seed: activeScenePlan.seed,
        themeId: theme.id,
        choreographyId: choreography.id,
        ambience: snapshot,
      });
      activeRenderer.apply(nextPlan);
      activeScenePlan = nextPlan;
      sceneApplyCount += 1;
    } catch (error) {
      modeStatus.textContent = `Motion ambience could not be applied: ${errorMessage(error)}`;
    }
  };

  const cleanupOwnedResources = async (): Promise<readonly unknown[]> => {
    const errors: unknown[] = [];
    if (sensorMotionQuery !== null && sensorMotionListener !== null) {
      try { sensorMotionQuery.removeEventListener("change", sensorMotionListener); }
      catch (error) { errors.push(error); }
    }
    sensorMotionQuery = null;
    sensorMotionListener = null;
    stopSensorFrames?.();
    stopSensorFrames = null;
    if (editor !== null) {
      if (terminalCleanupFailures.has("editor")) {
        errors.push(new Error("Editor cleanup previously failed and cannot be retried safely"));
      } else {
        try { editor.destroy(); editor = null; }
        catch (error) { terminalCleanupFailures.add("editor"); errors.push(error); }
      }
    }
    for (const host of [...ownedRenderers]) {
      try {
        host.destroy();
        if (!host.diagnostics().destroyed) throw new Error("Renderer cleanup is incomplete");
        ownedRenderers.delete(host);
      } catch (error) { errors.push(error); }
    }
    if (journey !== null) {
      if (terminalCleanupFailures.has("journey")) {
        errors.push(new Error("Journey cleanup previously failed and cannot be retried safely"));
      } else {
        try { journey.destroy(); journey = null; }
        catch (error) { terminalCleanupFailures.add("journey"); errors.push(error); }
      }
    }
    if (sensorControls !== null) {
      try { await sensorControls.destroy(); sensorControls = null; }
      catch (error) { errors.push(error); }
    }
    if (audioControls !== null) {
      try {
        await audioControls.destroy();
        audioControls = null;
        audioController = null;
      } catch (error) { errors.push(error); }
    }
    for (const cleanup of [...cleanups].reverse()) {
      try { cleanup(); cleanups.splice(cleanups.indexOf(cleanup), 1); }
      catch (error) { errors.push(error); }
    }
    return Object.freeze(errors);
  };

  function destroy(): Promise<void> {
    if (destroyPromise !== null) return destroyPromise;
    let resolveDestroy!: () => void;
    let rejectDestroy!: (error: unknown) => void;
    const sharedDestroy = new Promise<void>((resolve, reject) => {
      resolveDestroy = resolve;
      rejectDestroy = reject;
    });
    destroyPromise = sharedDestroy;
    retirementOwner.destroyPromise = sharedDestroy;
    retirementOwner.state = "retiring";
    globalRetirementOwner = retirementOwner;
    if (!composition.revoked) {
      composition.revoked = true;
      composition.destroyed = true;
      composition.publicationGeneration += 1;
      importGeneration += 1;
      teardownPending = true;
      const retirement = new Promise<void>((resolve) => { resolveRetirement = resolve; });
      teardownBarrier = Promise.all([teardownBarrier, retirement]).then(() => undefined);
      for (const surface of composition.app.querySelectorAll<HTMLElement>(
        "button, [data-north-star-import], [data-north-star-renderer], [data-north-star-draft-line]",
      )) {
        try { surface.replaceWith(surface.cloneNode(true)); } catch { /* retiring */ }
      }
      try { publicationSlot.replaceWith(publicationSlot.cloneNode(false)); } catch { /* retiring */ }
    }
    void (async (): Promise<void> => {
      const errors = await cleanupOwnedResources();
      if (errors.length > 0) {
        retirementOwner.errors = Object.freeze([...errors]);
        sessionStatus.textContent = `North Star cleanup is incomplete (${errors.length}); retry teardown.`;
        throw new AggregateError(errors, "North Star cleanup is incomplete");
      }
      pendingImport?.discard();
      pendingImport = null;
      if (exportUrl !== null) URL.revokeObjectURL(exportUrl);
      exportUrl = null;
      activeRenderer = null;
      activeScenePlan = null;
      if (activeComposition === composition) activeComposition = null;
      retirementOwner.state = "retired";
      retirementOwner.errors = Object.freeze([]);
      if (globalRetirementOwner === retirementOwner) globalRetirementOwner = null;
      resolveRetirement?.();
      resolveRetirement = null;
      teardownPending = false;
      if (remountRequested) {
        remountRequested = false;
        mountNorthStar();
      }
    })().then(resolveDestroy, (error: unknown) => {
      retirementOwner.state = "failed";
      retirementOwner.errors = Object.freeze([error]);
      sessionStatus.textContent = `North Star cleanup failed: ${errorMessage(error)}`;
      if (destroyPromise === sharedDestroy) destroyPromise = null;
      if (retirementOwner.destroyPromise === sharedDestroy) {
        retirementOwner.destroyPromise = null;
      }
      rejectDestroy(error);
    });
    return sharedDestroy;
  }

  listen(app, "click", (event) => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("button") : null;
    if (target === null) return;
    const mode = target.dataset.northStarMode;
    if (mode === "compose" || mode === "annotate" || mode === "repair"
        || mode === "weave" || mode === "stage") {
      setMode(mode);
      return;
    }
    const action = target.dataset.northStarAction;
    if (action === "capture-annotation") {
      if (editor === null || editor.mode !== "annotate") {
        modeStatus.textContent = "Select Annotate before capturing text.";
        return;
      }
      const result = editor.captureSelection(`annotation-${++annotationCounter}`);
      modeStatus.textContent = result.status === "accepted"
        ? "Annotation captured."
        : result.status === "repair-pending"
          ? "Annotation captured; repair choices are pending."
          : "Choose text inside the annotation poem first.";
    } else if (action === "save") saveSession();
    else if (action === "reload") {
      if (pendingImport !== null) {
        sessionStatus.textContent = "Resolve or discard the pending import before reloading.";
      } else resumeState("Saved session resumed.");
    } else if (action === "export") {
      saveSession();
      if (exportUrl !== null) URL.revokeObjectURL(exportUrl);
      exportUrl = URL.createObjectURL(new Blob([session.exportJSON()], { type: "application/json" }));
      const anchor = document.createElement("a");
      anchor.href = exportUrl;
      anchor.download = "summer-rain-session.json";
      anchor.click();
    } else if (action === "publication") void mountTerminalPublication();
  });

  listen(app, "input", (event) => {
    const item = event.target instanceof HTMLElement
      ? event.target.closest<HTMLElement>("[data-north-star-draft-line]")
      : null;
    if (item !== null) {
      const index = Number(item.dataset.northStarDraftLine);
      if (Number.isInteger(index) && index >= 0 && index < 3) {
        draftLines[index] = item.textContent ?? "";
        draftDirty = true;
      }
      return;
    }
    if (editor !== null && appMode === "compose") {
      draftLines = [...editor.state.lines];
      syncCanonicalDom();
    }
  });

  listen(app, "keydown", (event) => {
    if (!(event.target instanceof HTMLButtonElement)
        || event.target.dataset.northStarMode === undefined) return;
    const current = modeButtons.indexOf(event.target);
    let next = current;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = modeButtons.length - 1;
    else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      next = (current + 1) % modeButtons.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      next = (current - 1 + modeButtons.length) % modeButtons.length;
    } else return;
    event.preventDefault();
    modeButtons[next]?.click();
    modeButtons[next]?.focus();
  });

  listen(rendererSelect, "change", () => {
    rendererPreference = rendererSelect.value as SceneRendererKind;
    refreshStage();
  });

  listen(importInput, "change", () => {
    const file = importInput.files?.[0];
    if (file === undefined) return;
    importName.textContent = `Selected file: ${file.name}`;
    const generationAtStart = ++importGeneration;
    void file.text().then((raw) => {
      if (ownsComposition(composition, generation)
          && generationAtStart === importGeneration) importSession(raw);
    }).catch((error: unknown) => {
      if (ownsComposition(composition, generation)
          && generationAtStart === importGeneration) {
        sessionStatus.textContent = `Import could not be read: ${errorMessage(error)}`;
      }
    }).finally(() => {
      if (ownsComposition(composition, generation)
          && generationAtStart === importGeneration) importInput.value = "";
    });
  });

  for (const button of app.querySelectorAll<HTMLButtonElement>("[data-north-star-game]")) {
    listen(button, "click", () => {
      if (button.dataset.northStarGame === "solo-garden") {
        if (soloGarden === null) {
          soloGarden = createSoloGarden(session.state.updatedAt || PACK_REF, draftLines);
        }
        activeGame = "solo-garden";
      } else {
        if (wordTending === null) {
          wordTending = createWordTendingGame({
            seed: session.state.updatedAt || PACK_REF,
            players: ["Rain", "Moss"],
            lines: draftLines,
          });
        }
        activeGame = "word-tending";
      }
      renderActiveGame();
    });
  }

  let resolveConstructionRetirement: (() => void) | null = null;
  const cleanupConstruction = (): Promise<void> => {
    if (retirementOwner.destroyPromise !== null) return retirementOwner.destroyPromise;
    let resolveCleanup!: () => void;
    let rejectCleanup!: (error: unknown) => void;
    const sharedCleanup = new Promise<void>((resolve, reject) => {
      resolveCleanup = resolve;
      rejectCleanup = reject;
    });
    retirementOwner.destroyPromise = sharedCleanup;
    retirementOwner.state = "retiring";
    globalRetirementOwner = retirementOwner;
    if (resolveConstructionRetirement === null) {
      teardownPending = true;
      const retirement = new Promise<void>((resolve) => {
        resolveConstructionRetirement = resolve;
      });
      teardownBarrier = Promise.all([teardownBarrier, retirement]).then(() => undefined);
    }
    void (async (): Promise<void> => {
      const cleanupErrors = await cleanupOwnedResources();
      if (cleanupErrors.length > 0) {
        throw new AggregateError(
          cleanupErrors,
          "North Star construction rollback is incomplete",
        );
      }
      retirementOwner.state = "retired";
      retirementOwner.errors = Object.freeze([]);
      if (globalRetirementOwner === retirementOwner) globalRetirementOwner = null;
      resolveConstructionRetirement?.();
      resolveConstructionRetirement = null;
      teardownPending = false;
      if (remountRequested) {
        remountRequested = false;
        mountNorthStar();
      }
    })().then(resolveCleanup, (error: unknown) => {
      retirementOwner.state = "failed";
      retirementOwner.errors = Object.freeze([error]);
      if (retirementOwner.destroyPromise === sharedCleanup) {
        retirementOwner.destroyPromise = null;
      }
      sessionStatus.textContent = `North Star rollback failed: ${errorMessage(error)}`;
      rejectCleanup(error);
    });
    return sharedCleanup;
  };

  const rollbackConstruction = (cause: unknown): never => {
    composition.revoked = true;
    composition.destroyed = true;
    retirementOwner.cleanup = cleanupConstruction;
    void cleanupConstruction().catch(() => undefined);
    sessionStatus.textContent = `North Star could not start: ${errorMessage(cause)}`;
    throw cause;
  };

  try {
    const audioHost = requireElement<HTMLElement>(app, "[data-north-star-audio]");
    audioController = createProceduralAudioController({ seed: PACK_REF });
    audioControls = renderAudioControls(audioHost, audioController);
    sensorControls = mountSensorControls(
      requireElement<HTMLElement>(app, "[data-north-star-sensors]"),
    );
    sensorMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncSensorFrames = (): void => {
      stopSensorFrames?.();
      stopSensorFrames = createSensorFrameSubscription({
        reducedMotion: sensorMotionQuery?.matches ?? true,
        requestFrame: (callback) => window.requestAnimationFrame(callback),
        cancelFrame: (frame) => window.cancelAnimationFrame(frame),
        tick: applySensorAmbience,
      });
    };
    sensorMotionListener = syncSensorFrames;
    sensorMotionQuery.addEventListener("change", sensorMotionListener);
    syncSensorFrames();
    journey = createJourneyShell({
      root: document,
      emit: (action) => {
        if (action.type === "SAVE" && hasAuthority()) saveSession();
      },
    });
  } catch (error) {
    rollbackConstruction(error);
  }

  const debug: NorthStarDebug = Object.freeze({
    diagnostics: () => {
      const renderer: RendererHostDiagnostics | null = activeRenderer?.diagnostics() ?? null;
      return deepFreeze({
        initialized: !composition.destroyed,
        generation,
        mode: appMode,
        pack: packStatus,
        session: {
          loadStatus: session.loadStatus.status,
          persistedAt: session.state.updatedAt,
          importPending: pendingImport !== null,
        },
        renderer: {
          kind: renderer?.kind ?? null,
          attemptedKinds: renderer === null ? [] : [...renderer.attemptedKinds],
          reducedMotion: renderer?.reducedMotion ?? false,
          destroyed: renderer?.destroyed ?? true,
          terminalFailureCode: renderer?.terminalFailure?.code ?? null,
        },
        scene: {
          signature: activeScenePlan?.signature ?? null,
          breeze: activeScenePlan?.ambience.breeze ?? 0,
          rainBias: activeScenePlan?.ambience.rainBias ?? 0,
          applies: sceneApplyCount,
        },
        games: {
          active: activeGame,
          soloTurn: soloGarden?.turn ?? null,
          wordTurn: wordTending?.turn ?? null,
          wordPhase: wordTending?.phase ?? null,
        },
        capabilities: {
          audio: audioController?.snapshot.status ?? "off",
          sensors: sensorControls?.controller.status() ?? "off",
        },
      });
    },
    test: Object.freeze({
      forceRendererFailure: (kind: SceneRendererKind, forced: boolean) => {
        if (!hasAuthority()) return;
        if (forced) forcedRendererFailures.add(kind);
        else forcedRendererFailures.delete(kind);
        refreshStage();
      },
    }),
  });
  try {
    hydrateGames(session.state.games);
    mountEditor(session.state.editorMode);
    renderMode();
  } catch (error) {
    rollbackConstruction(error);
  }
  sessionStatus.textContent = malformedPersistedGamesRecovery.status === "recovered"
    ? "Malformed local game data was recovered; Summer Rain opened with a clean fallback."
    : malformedPersistedGamesRecovery.status === "clock-error"
      ? "Malformed local game data remains stored because the recovery clock is unavailable; Summer Rain opened with a clean in-memory fallback."
      : malformedPersistedGamesRecovery.status === "storage-error"
        ? "Malformed local game data remains stored because recovery storage is unavailable; Summer Rain opened with a clean in-memory fallback."
        : malformedPersistedGamesRecovery.status === "collision-exhausted"
          ? "Malformed local game data remains stored because all recovery collision keys are occupied; Summer Rain opened with a clean in-memory fallback."
    : foreignPersistedSession
      ? "A foreign local session was ignored; Summer Rain remains unchanged."
    : session.loadStatus.status === "loaded"
      ? "Local session loaded and resumed."
      : "New local session ready.";

  const pageHide = (event: PageTransitionEvent): void => {
    try {
      if (event.persisted) {
        const result = saveSession();
        if (result.status === "clock-error") {
          sessionStatus.textContent = "Page retirement save failed because the local clock is unavailable.";
        }
      }
    } catch (error) {
      sessionStatus.textContent = `Page retirement save failed: ${errorMessage(error)}`;
    } finally {
      void destroy().catch(() => undefined);
    }
  };
  window.addEventListener("pagehide", pageHide);
  cleanups.push(() => window.removeEventListener("pagehide", pageHide));

  activeComposition = composition;
  retirementOwner.state = "active";
  globalRetirementOwner = retirementOwner;
  nextCompositionGeneration = generation;
  window.NorthStarDebug = debug;
  void registerNorthStarWorker();
  void loadSummerRainPack().then(() => {
    if (ownsComposition(composition, generation)) packStatus = "ready";
  }).catch(() => {
    if (ownsComposition(composition, generation)) packStatus = "fallback";
  });
  return composition;
}

export function teardownNorthStar(): Promise<void> {
  const composition = activeComposition;
  if (composition !== null) return composition.destroy();
  if (globalRetirementOwner !== null) return globalRetirementOwner.cleanup();
  return teardownBarrier;
}

window.addEventListener("pageshow", (event: PageTransitionEvent) => {
  if (event.persisted) mountNorthStar();
});

try {
  mountNorthStar();
} catch (error) {
  const status = document.querySelector<HTMLElement>("[data-north-star-session-status]");
  if (status !== null) status.textContent = `Summer Rain could not open: ${errorMessage(error)}`;
}
